import { breakpoint } from '@/helpers/BreakpointHelper';
import React, { FC } from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const SkeletonBar = styled.div<{ width?: string; height?: string }>`
  display: inline-block;
  height: ${({ height }) => height || '20px'};
  width: ${({ width }) => width || '60%'};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;

// list type (공지사항)
const ListItem = styled.li`
  display: flex;
  padding: 32px 0;
  border-bottom: 1px solid #e5e6e8;
  ${breakpoint(640)} {
    padding: 16px 0;
  }
`;

const ListItemContent = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  ${breakpoint(640)} {
    display: block;
  }
`;

// card type (보도자료, 소식, 자료실, 파티포유)
const CardContainer = styled.ul<{ topPadding?: string }>`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -16px;
  margin-top: 32px;
  ${({ topPadding }) => topPadding && `padding-top: ${topPadding};`}
`;

const CardItem = styled.li<{ columns?: number; cardGap?: string; mobileCardWidth?: string; mobileCardGap?: string }>`
  padding: 0 16px;
  width: ${({ columns }) => (columns ? `${100 / columns}%` : '33.33%')};
  margin-bottom: ${({ cardGap }) => cardGap || '64px'};
  ${breakpoint(`tablet`)} {
    width: ${({ columns }) => (columns && columns > 3 ? '33.33%' : '50%')};
  }
  ${breakpoint(640)} {
    width: ${({ mobileCardWidth }) => mobileCardWidth || '50%'};
    margin-bottom: ${({ mobileCardGap, cardGap }) => mobileCardGap || cardGap || '48px'};
  }
`;

const CardImagePlaceholder = styled.div<{ imageHeight?: string; mobileImageHeight?: string }>`
  position: relative;
  ${({ imageHeight }) =>
    imageHeight
      ? `height: ${imageHeight};`
      : `padding-top: 100%;`}
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  ${({ mobileImageHeight }) =>
    mobileImageHeight
      ? `${breakpoint(640)} { height: ${mobileImageHeight}; padding-top: 0; }`
      : ''}
`;

const CardBody = styled.div`
  padding-top: 32px;
`;

// 검색바 스켈레톤
const SearchBarPlaceholder = styled.div<{ gap?: string; mobileGap?: string; mobilePaddingTop?: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-bottom: 32px;
  ${({ gap: gapValue }) => gapValue && `margin-bottom: ${gapValue};`}
  ${({ mobilePaddingTop }) => mobilePaddingTop !== false && `
    ${breakpoint('mobile')} {
      padding-top: 48px;
    }
  `}
  ${({ mobileGap }) => mobileGap && `
    ${breakpoint('mobile')} {
      margin-bottom: ${mobileGap};
    }
  `}
`;

const ListWrapper = styled.div``;

// database-list type (자료실 목록 - 가로형)
const DatabaseListItem = styled.div`
  display: flex;
  margin: 0 -16px;
  margin-bottom: 64px;
  justify-content: space-between;
  align-items: stretch;

  ${breakpoint(`tablet`)} {
    flex-wrap: wrap;
    margin-bottom: 72px;
  }
`;

const DatabaseListImage = styled.div`
  width: 304px;
  padding: 0 16px;
  flex-shrink: 0;

  ${breakpoint(`tablet`)} {
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
    padding: 0;
  }
`;

const DatabaseListImagePlaceholder = styled.div`
  padding-top: 136.46%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const DatabaseListContent = styled.div`
  width: calc(100% - 304px);
  padding: 22px 16px 0;
  max-width: 768px;

  ${breakpoint(`tablet`)} {
    width: 100%;
    padding: 0 16px;
    margin-top: 24px;
  }
`;

interface BoardSkeletonProps {
  type: 'list' | 'card' | 'database-list';
  rows?: number;
  columns?: number;
  imageHeight?: string;
  mobileImageHeight?: string;
  showSearchBar?: boolean;
  cardGap?: string;
  mobileCardGap?: string;
  mobileCardWidth?: string;
  searchBarGap?: string;
  searchBarMobileGap?: string;
  searchBarMobilePadding?: boolean;
  cardTopPadding?: string;
}

const BoardSkeleton: FC<BoardSkeletonProps> = ({ type, rows, columns, imageHeight, mobileImageHeight, showSearchBar, cardGap, mobileCardGap, mobileCardWidth, searchBarGap, searchBarMobileGap, searchBarMobilePadding, cardTopPadding }) => {
  const searchBar = showSearchBar ? (
    <SearchBarPlaceholder gap={searchBarGap} mobileGap={searchBarMobileGap} mobilePaddingTop={searchBarMobilePadding}>
      <SkeletonBar width="240px" height="56px" />
      <SkeletonBar width="80px" height="56px" />
    </SearchBarPlaceholder>
  ) : null;

  // 행마다 다른 너비로 자연스러운 느낌
  const listWidths = ['75%', '55%', '65%', '45%', '70%', '50%', '60%', '80%', '40%', '58%'];
  const cardTitleWidths = ['80%', '60%', '70%', '50%', '75%', '65%'];

  if (type === 'database-list') {
    const count = rows || 4;
    const descWidths = ['100%', '90%', '80%', '70%'];
    return (
      <>
        {searchBar}
        <div css="margin-top: 32px; background: #f8f9fa; padding: 46px 32px 35px;">
          <SkeletonBar width="220px" height="22px" css="display: block; margin-bottom: 24px;" />
          <div css="display: flex; flex-wrap: wrap; gap: 14px 12px;">
            <SkeletonBar width="64px" height="36px" />
            <SkeletonBar width="96px" height="36px" />
            <SkeletonBar width="80px" height="36px" />
            <SkeletonBar width="110px" height="36px" />
            <SkeletonBar width="72px" height="36px" />
            <SkeletonBar width="88px" height="36px" />
            <SkeletonBar width="104px" height="36px" />
            <SkeletonBar width="76px" height="36px" />
            <SkeletonBar width="92px" height="36px" />
            <SkeletonBar width="84px" height="36px" />
            <SkeletonBar width="100px" height="36px" />
          </div>
        </div>
        <div css="margin-top: 72px;">
          {Array.from({ length: count }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <DatabaseListItem key={`skeleton-dblist-${i}`}>
              <DatabaseListImage>
                <DatabaseListImagePlaceholder />
              </DatabaseListImage>
              <DatabaseListContent>
                <div css="display: flex; gap: 8px;">
                  <SkeletonBar width="60px" height="26px" />
                  <SkeletonBar width="80px" height="26px" />
                </div>
                <SkeletonBar width={cardTitleWidths[i % cardTitleWidths.length]} height="24px" css="margin-top: 24px; display: block;" />
                <SkeletonBar width={descWidths[0]} height="16px" css="margin-top: 8px; display: block;" />
                <SkeletonBar width={descWidths[1]} height="16px" css="margin-top: 6px; display: block;" />
                <SkeletonBar width={descWidths[2]} height="16px" css="margin-top: 6px; display: block;" />
                <SkeletonBar width={descWidths[3]} height="16px" css="margin-top: 6px; display: block;" />
              </DatabaseListContent>
            </DatabaseListItem>
          ))}
        </div>
      </>
    );
  }

  if (type === 'list') {
    const count = rows || 5;
    return (
      <>
        {searchBar}
        <ListWrapper>
          {Array.from({ length: count }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <ListItem key={`skeleton-list-${i}`}>
              <ListItemContent>
                <SkeletonBar width={listWidths[i % listWidths.length]} height="29px" />
                <SkeletonBar width="100px" css={`${breakpoint(640)} { margin-top: 8px; display: block; }`} />
              </ListItemContent>
            </ListItem>
          ))}
        </ListWrapper>
      </>
    );
  }

  // card type
  const count = rows || 6;
  return (
    <>
      {searchBar}
      <CardContainer topPadding={cardTopPadding}>
        {Array.from({ length: count }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <CardItem key={`skeleton-card-${i}`} columns={columns} cardGap={cardGap} mobileCardGap={mobileCardGap} mobileCardWidth={mobileCardWidth}>
            <CardImagePlaceholder imageHeight={imageHeight} mobileImageHeight={mobileImageHeight} />
            <CardBody>
              <SkeletonBar width={cardTitleWidths[i % cardTitleWidths.length]} height="24px" />
              <SkeletonBar
                width="40%"
                height="16px"
                css="margin-top: 16px; display: block;"
              />
            </CardBody>
          </CardItem>
        ))}
      </CardContainer>
    </>
  );
};

// 소식 배너 스켈레톤
const BannerSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  background: #f8f9fa;
  margin-bottom: 112px;

  ${breakpoint(`tablet`)} {
    margin-bottom: 40px;
  }
`;

const BannerSkeletonImage = styled.div`
  width: 39%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const BannerSkeletonContent = styled.div`
  width: 61%;
  padding: 104px 108px 60px 52px;

  ${breakpoint(1120)} {
    padding-left: 4.63vw;
    padding-right: 9.64vw;
  }
`;

export const BannerSkeleton: FC = () => (
  <BannerSkeletonWrapper>
    <BannerSkeletonImage />
    <BannerSkeletonContent>
      <SkeletonBar width="75%" height="32px" />
      <SkeletonBar width="50%" height="32px" css="margin-top: 8px; display: block;" />
      <SkeletonBar width="100%" height="16px" css="margin-top: 24px; display: block;" />
      <SkeletonBar width="95%" height="16px" css="margin-top: 8px; display: block;" />
      <SkeletonBar width="85%" height="16px" css="margin-top: 8px; display: block;" />
      <SkeletonBar width="90%" height="16px" css="margin-top: 8px; display: block;" />
      <SkeletonBar width="60%" height="16px" css="margin-top: 8px; display: block;" />
      <div css="margin-top: 16px; display: flex; gap: 8px;">
        <SkeletonBar width="60px" height="26px" />
        <SkeletonBar width="80px" height="26px" />
        <SkeletonBar width="70px" height="26px" />
      </div>
    </BannerSkeletonContent>
  </BannerSkeletonWrapper>
);

// 자료실 최신자료 스켈레톤
const DatabaseTopWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 128px;
  padding-bottom: 117px;

  ${breakpoint(`tablet`)} {
    gap: 40px;
  }
  ${breakpoint(`mobile`)} {
    gap: 0;
  }
`;

const DatabaseTopCard = styled.div`
  width: 288px;
  flex-shrink: 0;
  padding-top: 35px;

  ${breakpoint(`tablet`)} {
    &:nth-child(3) {
      display: none;
    }
  }
  ${breakpoint(`mobile`)} {
    width: 100%;
    &:nth-child(2) {
      display: none;
    }
  }
`;

const DatabaseTopImagePlaceholder = styled.div`
  padding-top: 136.46%;
  background: linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

export const DatabaseTopSkeleton: FC = () => (
  <DatabaseTopWrapper>
    {[0, 1, 2].map((i) => (
      <DatabaseTopCard key={i}>
        <DatabaseTopImagePlaceholder />
        <div css="padding-top: 17px; text-align: center;">
          <SkeletonBar width="80%" height="20px" />
        </div>
      </DatabaseTopCard>
    ))}
  </DatabaseTopWrapper>
);

// 스켈레톤 → 콘텐츠 전환 페이드인
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const FadeInWrapper = styled.div`
  animation: ${fadeIn} 200ms ease-in-out;
`;

export default BoardSkeleton;
